import React from 'react'
import { ClipboardList } from 'lucide-react'
import Badge from './ui/Badge'
import ConvictionBar from './ui/ConvictionBar'
import { formatPrice, formatNumber } from '../utils/format'
import { PRIVACY } from '../config/ondsConfig'

const TAG_TONE = { Primary: 'bull', Risk: 'caution', Aggressive: 'bear' }
const MASK = PRIVACY.mask

export default function TradeBlotter({ trades = [], locked = false }) {
  if (!trades.length) return null

  const sizeLabel = (t) => {
    if (locked || typeof t.sizeShares !== 'number') return `Adjust ${MASK} sh`
    return `${t.sizeShares > 0 ? 'Add' : 'Trim'} ${formatNumber(Math.abs(t.sizeShares))} sh`
  }

  return (
    <div className="onds-card p-5 md:p-6">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardList className="h-5 w-5" style={{ color: 'var(--onds-accent)' }} />
        <h2 className="text-lg font-bold">Trade Blotter</h2>
        <span className="text-sm text-[var(--onds-text-dim)]">actionable, committee-authorized orders</span>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {trades.map((t) => (
          <div key={t.id} className="rounded-xl p-4 flex flex-col gap-3" style={{ backgroundColor: 'var(--onds-surface)', border: '1px solid var(--onds-border)' }}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold">{t.title}</h3>
              <Badge tone={TAG_TONE[t.tag] ?? 'neutral'}>{t.tag}</Badge>
            </div>

            <p className="text-sm text-[var(--onds-text-dim)] leading-relaxed">{t.thesis}</p>

            <div className="grid grid-cols-3 gap-2 text-center">
              <Leg label="Entry" value={t.entry ? formatPrice(t.entry) : '-'} />
              <Leg label="Stop" value={t.stop ? formatPrice(t.stop) : '-'} tone="bear" />
              <Leg label="Target" value={t.target ? formatPrice(t.target) : '-'} tone="bull" />
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-[var(--onds-text-dim)]">{sizeLabel(t)}</span>
              <span className="text-[var(--onds-text-dim)]">{t.funding}</span>
              {t.rewardRisk && (
                <span className="font-semibold" style={{ color: 'var(--onds-bull)' }}>{t.rewardRisk}:1 R/R</span>
              )}
            </div>

            <div className="mt-auto pt-1">
              <ConvictionBar value={t.conviction} label={`Conviction · ${t.timeframe}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Leg({ label, value, tone = 'neutral' }) {
  const color = tone === 'bull' ? 'var(--onds-bull)' : tone === 'bear' ? 'var(--onds-bear)' : 'var(--onds-text)'
  return (
    <div className="rounded-lg py-2" style={{ backgroundColor: 'var(--onds-bg)' }}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--onds-text-dim)]">{label}</div>
      <div className="text-sm font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  )
}
